import { useEffect, useMemo, useState } from "react";
import { LayoutGrid, LogOut, Search, X } from "lucide-react";
import { ROLE_LABELS, useAuth } from "./auth";
import { APP_GROUPS } from "./app-catalog";
import { useAppNav } from "./app-nav";
import { t } from "@/lib/i18n";

/** Боковое меню-дублёр: те же разделы, что и на хабе, с поиском. */
export default function AppMenu() {
  const { user, signOut } = useAuth();
  const { open: openSection } = useAppNav();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return APP_GROUPS;
    return APP_GROUPS.map((g) => ({
      ...g,
      items: g.items.filter((i) => `${i.label} ${i.hint}`.toLowerCase().includes(q)),
    })).filter((g) => g.items.length > 0);
  }, [query]);

  const email = user?.email || "";
  const name = user?.name || email.split("@")[0] || t("Пользователь");
  const avatar = user?.avatarUrl || "";

  const go = (type: string) => {
    setOpen(false);
    setQuery("");
    openSection(type);
  };

  return (
    <>
      <div className="fixed bottom-24 start-4 z-50 flex items-center gap-2 md:bottom-6 md:start-6">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-border bg-card/95 px-4 py-3 text-sm font-semibold text-foreground backdrop-blur transition hover:border-primary/60 hover:text-primary"
          aria-label={t("Меню приложения")}
        >
          <LayoutGrid className="size-4 text-primary" aria-hidden />
          <span className="whitespace-nowrap">{t("Меню")}</span>
        </button>
        <button
          type="button"
          onClick={() => void signOut()}
          className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-border bg-card/95 px-4 py-3 text-sm font-semibold text-muted-foreground backdrop-blur transition hover:border-primary/60 hover:text-primary"
          aria-label={t("Выйти из аккаунта")}
          title={t("Выйти из аккаунта")}
        >
          <LogOut className="size-4" aria-hidden />
          <span className="whitespace-nowrap">{t("Выйти")}</span>
        </button>
      </div>

      {open ? (
        <div className="fixed inset-0 z-[70] flex">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setOpen(false)} aria-hidden />
          <aside
            className="relative flex h-full w-[360px] max-w-[88vw] flex-col border-e border-border bg-background"
            role="dialog"
            aria-modal="true"
            aria-label={t("Разделы приложения")}
          >
            <div className="flex items-start justify-between gap-3 border-b border-border px-4 py-4">
              <div className="flex min-w-0 items-center gap-3">
                {avatar ? (
                  <img src={avatar} alt={name} className="size-10 flex-none rounded-full object-cover" />
                ) : (
                  <span className="grid size-10 flex-none place-items-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
                    {name.slice(0, 1).toUpperCase()}
                  </span>
                )}
                <span className="min-w-0">
                  <b className="block truncate text-sm font-semibold">{name}</b>
                  <span className="block truncate text-xs text-muted-foreground">{email}</span>
                  {user ? (
                    <span className="mt-1 inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[.12em] text-muted-foreground">
                      {ROLE_LABELS[user.role]}
                    </span>
                  ) : null}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="grid size-9 flex-none cursor-pointer place-items-center rounded-btn text-muted-foreground transition hover:bg-muted hover:text-foreground"
                aria-label={t("Закрыть меню")}
              >
                <X className="size-4" aria-hidden />
              </button>
            </div>

            <div className="border-b border-border px-4 py-3">
              <div className="flex items-center gap-2 rounded-btn border border-border bg-card px-3">
                <Search className="size-4 flex-none text-muted-foreground" aria-hidden />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t("Найти раздел")}
                  className="h-10 w-full min-w-0 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
                />
              </div>
            </div>

            <nav className="flex-1 overflow-y-auto px-3 py-3">
              {groups.length === 0 ? (
                <p className="px-2 py-6 text-center text-sm text-muted-foreground">{t("Ничего не нашлось")}</p>
              ) : (
                groups.map((g) => (
                  <div key={g.title} className="mb-4">
                    <p className="mb-1.5 px-2 text-[11px] font-semibold uppercase tracking-[.14em] text-muted-foreground">{g.title}</p>
                    <ul className="flex flex-col">
                      {g.items.map((item) => (
                        <li key={item.type}>
                          <button
                            type="button"
                            onClick={() => go(item.type)}
                            className="flex w-full cursor-pointer items-center gap-3 rounded-btn px-2.5 py-2.5 text-left text-sm font-medium text-foreground/85 transition hover:bg-muted hover:text-foreground"
                          >
                            <span className="grid size-7 flex-none place-items-center rounded-btn bg-primary/10 text-primary" aria-hidden>
                              <item.icon className="size-4" />
                            </span>
                            <span className="min-w-0">
                              <span className="block truncate">{item.label}</span>
                              <span className="block truncate text-[11px] text-muted-foreground">{item.hint}</span>
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))
              )}
            </nav>

            <div className="border-t border-border px-4 py-3">
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  void signOut();
                }}
                className="flex w-full cursor-pointer items-center gap-2.5 rounded-btn border border-border px-3 py-2.5 text-sm font-semibold text-muted-foreground transition hover:border-primary/50 hover:text-foreground"
              >
                <LogOut className="size-4" aria-hidden />
                {t("Выйти из аккаунта")}
              </button>
            </div>
          </aside>
        </div>
      ) : null}
    </>
  );
}